<template>
  <div class="container-fluid">
    <div class="row" id="app">

      <!-- Input Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <TopbarNav :pageTitle="'Snow Load Calculation'" :showHome="true" />
        <hr />

        <!-- Roof Type Selection -->
        <div class="row">
          <div class="col-6">Roof Type</div>
          <div class="col-6">
            <div>
              <input type="radio" v-model="roof_type" value="flat" />
              Flat Roof
              <span class="fst-italic comment"> — Compute p_f only</span>
            </div>
            <div>
              <input type="radio" v-model="roof_type" value="sloped" />
              Sloped Roof
              <span class="fst-italic comment"> — Compute both p_f and p_s</span>
            </div>
          </div>
        </div>
        <p class="fst-italic comment">
          Select whether to calculate flat roof snow load only, or include sloped roof snow load.
        </p>
        <hr />

        <!-- Flat Roof Factors -->
        <div class="row">
          <div class="col-6">Exposure Factor, C<sub>e</sub></div>
          <div class="col-6">
            <input type="number" v-model.number="C_e" step="0.05" min="0" />
          </div>
        </div>
        <p class="fst-italic comment">As determined from the applicable exposure table.</p>

        <div class="row">
          <div class="col-6">Thermal Factor, C<sub>t</sub></div>
          <div class="col-6">
            <input type="number" v-model.number="C_t" step="0.05" min="0" />
          </div>
        </div>
        <p class="fst-italic comment">As determined from the applicable thermal factor table.</p>

        <div class="row">
          <div class="col-6">Importance Factor, I<sub>s</sub></div>
          <div class="col-6">
            <input type="number" v-model.number="I_s" step="0.05" min="0" />
          </div>
        </div>
        <p class="fst-italic comment">As prescribed in Section 7.3.3.</p>

        <div class="row">
          <div class="col-6">Ground Snow Load, p<sub>g</sub></div>
          <div class="col-6">
            <input type="number" v-model.number="p_g" step="1" min="0" /><span>psf</span>
          </div>
        </div>
        <p class="fst-italic comment">
          Ground snow load from map, table, or site-specific analysis.
        </p>

        <!-- Sloped Roof Factor (conditional on roof_type) -->
        <div v-if="roof_type === 'sloped'">
          <hr />
          <div class="row">
            <div class="col-6">Roof Slope Factor, C<sub>s</sub></div>
            <div class="col-6">
              <input type="number" v-model.number="C_s" step="0.05" min="0" max="1" />
            </div>
          </div>
          <p class="fst-italic comment">Applicable only for sloped roof snow load calculation.</p>
        </div>
      </div>

      <!-- Report Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <p class="fst-italic">ASCE 7 Sections 7.3 and 7.4</p>
        <hr />

        <!-- Flat Roof Snow Load -->
        <p><strong>Flat Roof Snow Load</strong></p>
        <p class="fst-italic comment">Eq. 7.3-1</p>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'p_f = 0.7 \\cdot C_e \\cdot C_t \\cdot I_s \\cdot p_g'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="pfSubstitutionExpr" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'p_f = ' + p_f.toFixed(2) + ' \\ \\text{psf}'" />
          </div>
        </div>
        <hr />

        <!-- Sloped Roof Snow Load (conditional on roof_type) -->
        <div v-if="roof_type === 'sloped'">
          <p><strong>Sloped Roof Snow Load</strong></p>
          <p class="fst-italic comment">Eq. 7.4-1</p>
          <div class="row">
            <div class="col-12">
              <KaTeXComponent :expression="'p_s = C_s \\cdot p_f'" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <KaTeXComponent :expression="psSubstitutionExpr" />
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <KaTeXComponent :expression="'p_s = ' + p_s.toFixed(2) + ' \\ \\text{psf}'" />
            </div>
          </div>
          <hr />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import TopbarNav from '@/components/TopbarNav.vue'

export default {
  name: 'SnowLoadASCE716',
  components: { TopbarNav },
  data() {
    return {
      roof_type: 'flat',
      C_e: 1.0,
      C_t: 1.0,
      I_s: 1.0,
      p_g: 30.0,
      C_s: 1.0,
    }
  },
  computed: {
    p_f() {
      return 0.7 * (this.C_e || 0) * (this.C_t || 0) * (this.I_s || 0) * (this.p_g || 0)
    },
    p_s() {
      return this.roof_type === 'sloped' ? (this.C_s || 0) * this.p_f : 0
    },
    pfSubstitutionExpr() {
      return (
        'p_f = 0.7 \\cdot ' +
        (this.C_e || 0).toFixed(2) +
        ' \\cdot ' +
        (this.C_t || 0).toFixed(2) +
        ' \\cdot ' +
        (this.I_s || 0).toFixed(2) +
        ' \\cdot ' +
        (this.p_g || 0).toFixed(2) +
        ' \\ \\text{psf}'
      )
    },
    psSubstitutionExpr() {
      return (
        'p_s = ' +
        (this.C_s || 0).toFixed(2) +
        ' \\cdot ' +
        this.p_f.toFixed(2) +
        ' \\ \\text{psf}'
      )
    },
  },
}
</script>
